import { TableCell, TableRow, Select, MenuItem, Chip, TextField, Dialog, DialogTitle, DialogContent, IconButton } from '@mui/material';
import { CheckCircle, EditRounded, RemoveRedEye, HourglassEmpty, VisibilityOutlined, Close } from '@mui/icons-material';
import { EyeOutlined, EditOutlined } from '@ant-design/icons';
import React, { memo, useState } from 'react';
import styled from 'styled-components';
import { handleUpdateTeacherSupportForm } from 'store/logs/thunks';
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';
import TeacherSupportForm from 'containers/admin/OwePages/TeacherSupportFrom';

const normalizeStatusForUI = (raw) => {
  switch ((raw || '').toLowerCase()) {
    case 'not-started':
    case 'not started':
      return 'Not started';
    case 'in-progress':
    case 'in progress':
      return 'In progress';
    case 'under-review':
    case 'under review':
      return 'Under review';
    case 'completed':
      return 'Completed';
    default:
      return 'Not started';
  }
};

const normalizeStatusForAPI = (uiVal) => {
  switch ((uiVal || '').toLowerCase()) {
    case 'not started':
      return 'not-started';
    case 'in progress':
      return 'in-progress';
    case 'under review':
      return 'under-review';
    case 'completed':
      return 'completed';
    default:
      return 'not-started';
  }
};

const TeacherSupportTableRow = ({ row, sequenceNo, onUpdateItem }) => {
  const showAlert = useAlert();
  const [notes, setNotes] = useState(row?.note || '');
  const [status, setStatus] = useState(normalizeStatusForUI(row?.status));
  const [isUpdating, setIsUpdating] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openEditForm, setOpenEditForm] = useState(false);

  const handleStatusChange = async (newStatus) => {
    const oldStatus = status;
    setStatus(newStatus);
    setIsUpdating(true);
    
    try {
      const updateData = {
        status: normalizeStatusForAPI(newStatus),
        note: notes,
      };
      
      const result = await handleUpdateTeacherSupportForm(row.id, updateData);
      
      if (result && result.data) {
        showAlert('Status updated successfully', 'success');
        if (onUpdateItem) {
          onUpdateItem(row.id, { status: newStatus });
        }
      } else {
        showAlert('Failed to update status', 'error');
        setStatus(oldStatus);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      showAlert('Error updating status', 'error');
      setStatus(oldStatus);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleNotesBlur = async () => {
    if (notes === row?.note) return;
    
    setIsUpdating(true);
    const oldNotes = row?.note || '';
    
    try {
      const updateData = {
        status: normalizeStatusForAPI(status),
        note: notes,
      };
      
      const result = await handleUpdateTeacherSupportForm(row.id, updateData);
      
      if (result && result.data) {
        showAlert('Notes updated successfully', 'success');
        if (onUpdateItem) {
          onUpdateItem(row.id, { note: notes });
        }
      } else {
        showAlert('Failed to update notes', 'error');
        setNotes(oldNotes);
      }
    } catch (error) {
      console.error('Error updating notes:', error);
      showAlert('Error updating notes', 'error');
      setNotes(oldNotes);
    } finally {
      setIsUpdating(false);
    }
  };

  const getStatusIcon = (statusText) => {
    switch (statusText) {
      case 'Completed':
        return <CheckCircle style={{ fontSize: 16 }} />;
      case 'In progress':
        return <EditRounded style={{ fontSize: 16 }} />;
      case 'Under review':
        return <RemoveRedEye style={{ fontSize: 16 }} />;
      case 'Not started':
        return <HourglassEmpty style={{ fontSize: 16 }} />;
      default:
        return null;
    }
  };

  const getStatusColor = (statusText) => {
    switch (statusText) {
      case 'Completed':
        return { bg: '#22c55e', color: 'white' };
      case 'In progress':
        return { bg: '#fbbf24', color: 'black' };
      case 'Under review':
        return { bg: '#60a5fa', color: 'white' };
      case 'Not started':
        return { bg: '#9ca3af', color: 'white' };
      default:
        return { bg: '#e5e7eb', color: 'black' };
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  return (
    <>
    <StyledTableRow>
      <StyledTableCell>{sequenceNo}.</StyledTableCell>
      <StyledTableCell>
        {row?.teacher && (
          <TeacherNameWrapper>
            <span>{row.teacher}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '8px' }}>
            <EyeOutlined 
              style={{ fontSize: 18, color: '#17a2b8', cursor: 'pointer' }} 
              onClick={() => setOpenDialog(true)}
            />
            <EditOutlined 
              style={{ fontSize: 18, color: '#000', cursor: 'pointer' }} 
              onClick={() => setOpenEditForm(true)}
            />
          </div>
          </TeacherNameWrapper>
        )}
      </StyledTableCell>
      <StyledTableCell>
        <SchoolNameWrapper>
          <SchoolNameText readOnly>{row?.district || '8'}</SchoolNameText>
        </SchoolNameWrapper>
      </StyledTableCell>
      <StyledTableCell>
        <DateBadge>{formatDate(row?.date_time)}</DateBadge>
      </StyledTableCell>
      <StyledTableCell>
        <Select
          value={status}
          onChange={(e) => handleStatusChange(e.target.value)}
          variant="outlined"
          size="small"
          disabled={isUpdating}
          sx={{
            minWidth: 150,
            height: 32,
            borderRadius: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',

            '& .MuiSelect-select': {
              padding: 0,
              lineHeight: '32px',
              fontSize: '12px',
              textAlign: 'center',
            },

            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
          }}
        //   renderValue={(selected) => (
        //     <StatusChip
        //       icon={getStatusIcon(selected)}
        //       label={selected}
        //       style={{
        //         backgroundColor: getStatusColor(selected).bg,
        //         color: getStatusColor(selected).color,
        //         height: 28,
        //         borderRadius: 14,
        //       }}
        //     />
        //   )
        // }
        >


          <MenuItem value="Not started">
            <StatusChip
              // icon={<HourglassBottomRoundedIcon style={{ fontSize: 16, color: '#000000' }} />}
              label="⏳ Not started"
              style={{ backgroundColor: '#e0e0e0', color: '#000000' }}
              sx={{
                '& .MuiChip-icon': {
                  color: 'white',
                },
                width: '150px',
              }}
            />
          </MenuItem>
          <MenuItem value="In progress">
            <StatusChip
              // icon={<EditRounded style={{ fontSize: 16, color: '#7b5800' }} />}
              label="✏️ In progress"
              style={{ backgroundColor: '#f2ebd7', color: '#7b5800' }}
              sx={{
                '& .MuiChip-icon': {
                  color: 'white',
                },
                width: '150px',
              }}
            />
          </MenuItem>
          {/* <MenuItem value="blocked">
            <StatusChip
              // icon={<EditRounded style={{ fontSize: 16, color: '#7b5800' }} />}
              label="⛔️ Blocked"
              style={{ backgroundColor: '#fdeaea', color: '#d93025' }}
              sx={{
                '& .MuiChip-icon': {
                  color: 'white',
                },
                width: '150px',
              }}
            />
          </MenuItem> */}
          <MenuItem value="Under review">
            <StatusChip
              // icon={<RemoveRedEye style={{ fontSize: 16, color: '#0b57d0' }} />}
              label="👀 Under review"
              style={{ backgroundColor: '#e8f0fe', color: '#0b57d0' }}
              sx={{
                '& .MuiChip-icon': {
                  color: 'white',
                },
                width: '150px',
              }}
            />
          </MenuItem>
          <MenuItem value="Completed">
            <StatusChip
              label="✅ Completed"
              style={{ backgroundColor: '#e8f5e9', color: '#0b7a1c' }}
              sx={{
                '& .MuiChip-icon': {
                  color: 'white',
                  textAlign: 'left',
                },
                width: '150px',
              }}
            />
          </MenuItem>
        </Select>
      </StyledTableCell>
      <StyledTableCell>
        <NotesTextField
          placeholder="Add here"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={handleNotesBlur}
          variant="standard"
          size="small"
          disabled={isUpdating}
          InputProps={{
            disableUnderline: true,
          }}
        />
      </StyledTableCell>
    </StyledTableRow>

    <Dialog 
      open={openDialog} 
      onClose={() => setOpenDialog(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '20px', fontWeight: 600 }}>Teacher Support Details</span>
          <IconButton onClick={() => setOpenDialog(false)} size="small">
            <Close />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent>
        <DetailsContainer>
          <DetailRow>
            <DetailLabel>Teacher Name:</DetailLabel>
            <DetailValue>{row?.teacher || '-'}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>District:</DetailLabel>
            <DetailValue>{row?.district || '-'}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>Date & Time:</DetailLabel>
            <DetailValue>{row?.date_time ? new Date(row.date_time).toLocaleString() : '-'}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>Duration (Minutes):</DetailLabel>
            <DetailValue>{row?.duration_minutes || '-'}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>Status:</DetailLabel>
            <DetailValue>
             {normalizeStatusForUI(row?.status)}
            </DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>Reason for Meeting:</DetailLabel>
            <DetailValue>{row?.reason_for_meeting || '-'}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>Solution:</DetailLabel>
            <DetailValue>{row?.solution || '-'}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>Support Member:</DetailLabel>
            <DetailValue>{row?.support_member || '-'}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>Meeting Link:</DetailLabel>
            <DetailValue>
              {row?.meeting_link ? (
                <a href={row.meeting_link} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2' }}>
                  {row.meeting_link}
                </a>
              ) : '-'}
            </DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>JIRA URL:</DetailLabel>
            <DetailValue>
              {row?.jira_url ? (
                <a href={row.jira_url} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2' }}>
                  {row.jira_url}
                </a>
              ) : '-'}
            </DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>Notes:</DetailLabel>
            <DetailValue>{row?.note || '-'}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>Created At:</DetailLabel>
            <DetailValue>{row?.created_at ? new Date(row.created_at).toLocaleString() : '-'}</DetailValue>
          </DetailRow>
        </DetailsContainer>
      </DialogContent>
    </Dialog>

    <TeacherSupportForm 
      visible={openEditForm}
      onClose={() => {
        setOpenEditForm(false);
      }}
      editData={row}
      onUpdateSuccess={(updatedData) => {
        if (onUpdateItem && updatedData) {
          const fieldsToUpdate = {
            district: updatedData.district,
            teacher: updatedData.teacher,
            date_time: updatedData.date_time,
            duration_minutes: updatedData.duration_minutes,
            reason_for_meeting: updatedData.reason_for_meeting,
            solution: updatedData.solution,
            support_member: updatedData.support_member,
            meeting_link: updatedData.meeting_link,
            jira_url: updatedData.jira_url,
            status: updatedData.status,
            note: updatedData.note,
          };
          onUpdateItem(row.id, fieldsToUpdate);
          if (updatedData.status) {
            setStatus(normalizeStatusForUI(updatedData.status));
          }
          if (updatedData.note !== undefined) {
            setNotes(updatedData.note || '');
          }
        }
      }}
    />
  </>
  );
};

export default memo(TeacherSupportTableRow);

const StyledTableRow = styled(TableRow)`
  &:hover {
    background-color: #f9fafb;
  }
`;

const StyledTableCell = styled(TableCell)`
  text-align: left;
  font-size: 14px;
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
  border-right: 1px solid #e5e7eb;
  
  &:last-child {
    border-right: none;
  }
`;

const TeacherNameWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: space-between;
`;

const SchoolNameWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SchoolNameInput = styled.input`
  padding: 4px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background-color: #f3f4f6;
  text-align: center;
  width: 60px;
  font-size: 14px;
  outline: none;
`;

const SchoolNameText = styled.span`
  font-size: 14px;
  color: #374151;
  white-space: nowrap;
`;

const StatusChip = styled(Chip)`
  font-size: 13px;
  font-weight: 500;
  border-radius: 14px;
`;

const NotesTextField = styled(TextField)`
  width: 100%;
  
  input {
    font-size: 14px;
    color: #000;
    
    &::placeholder {
      color: rgb(0, 0, 0);
    }
  }
`;

const DateBadge = styled.div`
  background-color: #e5e7eb;
  padding: 6px 16px;
  border-radius: 20px;
  display: inline-block;
  font-size: 14px;
  color: #000;
  text-align: center;
`;

const DetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const DetailRow = styled.div`
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 16px;
  padding: 12px;
  background-color: #f9fafb;
  border-radius: 8px;
  align-items: start;
`;

const DetailLabel = styled.div`
  font-weight: 600;
  color: #374151;
  font-size: 14px;
`;

const DetailValue = styled.div`
  color: #1f2937;
  font-size: 14px;
  word-break: break-word;
`;
